import "./Status.css";
import { tx } from "../i18n";

/** 공통 v3 의 톤 이름 — b3x-in 의 톤(muted · accent · error)을 공통 `.b-badge-*` 로 잇는 한 자리. */
const BADGE: Record<string, string> = {
  muted: "",
  accent: "b-badge-primary",
  success: "b-badge-success",
  warning: "b-badge-warning",
  error: "b-badge-danger",
  info: "b-badge-info",
};

/** 상태 표식 — 공통 `.b-badge`(sm 24 · 알약). **색만으로 가르지 않는다** — 글리프(모양)와 한글 이름이 늘 함께 간다.
 *  색을 못 보는 사람에게도, 흑백 스크린샷에서도 같은 것이 읽혀야 한다.
 *
 *  글리프는 한 표에서만 고른다. 화면마다 아이콘을 새로 고르면 같은 상태가 화면마다
 *  다르게 보인다 — b3x-pr 이 상태 아이콘을 한 표(`STATUS_ICON`)로 모은 이유다. */
export const STATUS = {
  todo: { label: tx("issue.status.todo"), glyph: "○", tone: "muted" },
  ready: { label: tx("issue.status.ready"), glyph: "◔", tone: "info" },
  "in-progress": { label: tx("issue.status.inProgress"), glyph: "◑", tone: "info" },
  review: { label: tx("issue.status.review"), glyph: "◇", tone: "warning" },
  blocked: { label: tx("issue.status.blocked"), glyph: "✋", tone: "error" },
  done: { label: tx("issue.status.done"), glyph: "●", tone: "success" },
  cancelled: { label: tx("issue.status.cancelled"), glyph: "⊘", tone: "muted" },
} as const;

export type StatusKey = keyof typeof STATUS;
export const STATUS_KEYS = Object.keys(STATUS) as StatusKey[];

export function StatusPill({
  status,
  working,
}: {
  status: StatusKey;
  /** **「도는 중」은 열린 세션이지 상태가 아니다.** 상태에서 파생시키지 마라 —
   *  b3x-pr 은 `in-progress` 를 돌려서 몇 시간 전에 죽은 세션까지 돌게 만들었다. */
  working?: boolean;
}) {
  const s = STATUS[status];
  return (
    <span
      className={["b-badge", BADGE[s.tone], "pill", working ? "is-working" : ""].filter(Boolean).join(" ")}
      aria-label={working ? tx("issue.workingLabel", { label: s.label }) : undefined}
      title={working ? tx("issue.workingTip") : undefined}
    >
      <span className="pill__glyph" aria-hidden="true">
        {s.glyph}
      </span>
      {s.label}
      {working ? ` · ${tx("issue.working")}` : null}
    </span>
  );
}

/** 값을 다는 칩(필터·꼬리표). 상태가 아니므로 톤은 부르는 쪽이 고른다. */
export function Chip({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "accent" | "success" | "warning" | "error" | "info";
}) {
  return <span className={["b-badge", BADGE[tone]].filter(Boolean).join(" ")}>{children}</span>;
}
